import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);

  // Tracks which userId is currently being fetched so that the getSession() call
  // and the onAuthStateChange INITIAL_SESSION event don't both trigger fetchProfile
  // and race against each other, causing a brief null/error flash.
  const fetchingForRef = useRef(null);

  useEffect(() => {
    // onAuthStateChange fires synchronously with INITIAL_SESSION on mount when a
    // persisted session exists, so it handles the startup case on its own.
    // getSession() is kept as a fallback for environments where the event is delayed.
    supabase.auth.getSession().then(({ data: { session } }) => {
      const uid = session?.user?.id ?? null;
      if (!uid) {
        setUser(null);
        setLoading(false);
        return;
      }
      setUser(session.user);
      // Only fetch if onAuthStateChange hasn't already started a fetch for this user
      if (fetchingForRef.current !== uid) fetchProfile(uid);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null;
      setUser(session?.user ?? null);
      if (uid) {
        fetchProfile(uid);
      } else {
        fetchingForRef.current = null;
        setProfile(null);
        setProfileError(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchProfile(userId, attempt = 1) {
    // Deduplicate: if we're already fetching for this exact userId, skip.
    if (fetchingForRef.current === userId) return;
    fetchingForRef.current = userId;

    if (attempt === 1) setProfileError(null);

    const { data, error } = await supabase
      .from('profiles')
      .select([
        'id', 'role', 'status', 'legal_name', 'email', 'phone',
        'badge_number', 'badge_expiration', 'badge_expiration_na',
        'emergency_name', 'emergency_phone', 'emergency_relation',
        'onboarding_complete', 'created_at', 'updated_at',
        'officer_type', 'first_name', 'last_name', 'city', 'state', 'zip', 'shirt_size',
        'referred_by', 'department_name', 'department_start',
        'override_approved', 'rejection_note', 'dl_back_url',
        'sms_prefs', 'rank', 'sworn_attested_at', 'sworn_attestation_due',
        'security_license_number', 'security_license_expiry', 'security_license_last_notified_days',
        'firearms_training_license', 'fcc_number', 'fcc_expiration',
        'fcc_revoked', 'fcc_revoked_at',
        'terms_accepted', 'terms_accepted_at', 'terms_version', 'terms_signature',
        'w9_legal_name', 'w9_business_name', 'w9_tax_classification',
        'w9_tax_classification_llc', 'w9_tax_classification_other',
        'w9_exempt_payee_code', 'w9_fatca_code',
        'w9_tin_type', 'w9_certified', 'w9_signed_at', 'w9_completed',
        'pin_enabled', 'pin_hash', 'pin_set_at',
        'classification', 'years_on_job', 'department',
        'former_department', 'years_sworn', 'rank_at_retirement',
        'app_installed_at',
      ].join(','))
      .eq('id', userId)
      .maybeSingle();

    // Guard: if the user changed while we were fetching, discard stale result
    if (fetchingForRef.current !== userId) return;

    if (error) {
      console.error(`fetchProfile error (attempt ${attempt}):`, error.code, error.message);
      setProfileError({ code: error.code, message: error.message });
      setProfile(null);
      fetchingForRef.current = null;
      setLoading(false);
    } else if (data) {
      setProfile(data);
      setProfileError(null);
      fetchingForRef.current = null;
      setLoading(false);
    } else {
      // No rows returned. Could be:
      //  (a) Race condition — email was just confirmed but the trigger that creates
      //      the profile row hasn't committed yet (common ~1s window).
      //  (b) Genuine missing profile or RLS block.
      // Retry once after 1 second before surfacing an error.
      console.warn(`fetchProfile: no profile row for ${userId} (attempt ${attempt})`);
      if (attempt === 1) {
        // Release lock so the retry can run. Keep loading=true so the UI
        // shows "Loading your profile…" rather than an error during the wait.
        fetchingForRef.current = null;
        setTimeout(() => fetchProfile(userId, 2), 1000);
      } else {
        console.error(`fetchProfile: no profile after ${attempt} attempts for ${userId}`);
        setProfileError({ code: 'NO_ROWS', message: `No profile row found after ${attempt} attempts.` });
        setProfile(null);
        fetchingForRef.current = null;
        setLoading(false);
      }
    }
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  const value = { user, profile, loading, profileError, signIn, signOut, fetchProfile };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
